import type { CustomerRestrictionInput, CustomerRestrictionView } from '@chukua/common';
import { CUSTOMER_RESTRICTION_LABELS } from '@chukua/common';
import { useEffect, useState } from 'react';
import { useToast } from '../../../shared/hooks/use-toast';
import { addCustomerRestriction, listCustomerRestrictions, removeCustomerRestriction, updateCustomerRestriction } from '../customers.api';

export type CustomerRestrictionsState = { status: 'loading' } | { status: 'loaded'; restrictions: CustomerRestrictionView[] } | { status: 'error' };

/** A customer's restrictions on what may carry their cargo (gitaumoses4/chukua#48), re-fetched after every change. */
export function useCustomerRestrictions(customerId: string) {
  const [state, setState] = useState<CustomerRestrictionsState>({ status: 'loading' });
  const [saveError, setSaveError] = useState<string | null>(null);
  const toast = useToast();

  useEffect(() => {
    let cancelled = false;
    listCustomerRestrictions(customerId)
      .then((restrictions) => {
        if (!cancelled) setState({ status: 'loaded', restrictions });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [customerId]);

  async function load(): Promise<void> {
    try {
      setState({ status: 'loaded', restrictions: await listCustomerRestrictions(customerId) });
    } catch {
      setState({ status: 'error' });
    }
  }

  /** Returns whether it saved, so the form can stay open with its error showing when it did not. */
  async function save(restrictionId: string | null, input: CustomerRestrictionInput): Promise<boolean> {
    setSaveError(null);
    try {
      if (restrictionId) await updateCustomerRestriction(customerId, restrictionId, input);
      else await addCustomerRestriction(customerId, input);
      await load();
      toast.success(`${CUSTOMER_RESTRICTION_LABELS[input.kind]} ${restrictionId ? 'updated' : 'added'}`);
      return true;
    } catch {
      setSaveError('Could not save — check the value and try again.');
      return false;
    }
  }

  async function remove(restriction: CustomerRestrictionView): Promise<void> {
    const label = CUSTOMER_RESTRICTION_LABELS[restriction.kind];
    try {
      await removeCustomerRestriction(customerId, restriction.id);
      await load();
      toast.success(`${label} removed`);
    } catch {
      toast.error(`Couldn't remove ${label}`);
    }
  }

  return { state, saveError, clearSaveError: () => setSaveError(null), save, remove };
}
